// Turn the backend's TableCommand into a grounded, confirmable proposal. GROUNDING happens against the
// page's LIVE columns (from app_context.table) — a column the user names that the table doesn't have, or a
// phrase that matches several, becomes a clarification, never a wrong action. Cheap/reversible ops
// (sort/pin/hide/page-size) are flagged `autoApply` so the card can apply them without a confirm click;
// the resets + export flag `autoApply:false` so the card confirms first (the plan's UX matrix, from
// TABLE_ACTIONS). Mirrors chartProposal/navProposal in shape so FilterAssistant renders it the same way.
import {
    TABLE_ACTIONS,
    RECENT_SORT_AXES,
    PAGE_SIZE_OPTIONS,
    EXPORT_FORMATS,
    buildTableColumns,
    groundColumn,
    groundValue,
    parseWildcardValue,
    hasThresholdOperator,
    exportOptionsOf,
    groundExportFormat,
    detectExportMode,
    groundExportVariants,
    groundExportBreakdown,
    detectExportFormat,
    detectIncludeSoftCost,
    normalizeText
} from "./tableCatalog";
import { formatNumericValue } from "../../core/numberFormat";

const FORMAT_LABEL = { excel: "Excel", word: "Word", pdf: "PDF" };

// Short human phrasing for a wildcard (threshold) operator, for the confirm line. Ids match the grid's
// wildcard operators (utils/wildCardFilterUtils.js).
const WILDCARD_OP_LABEL = {
    greater_than: "greater than",
    greater_than_or_equal: "at least",
    less_than: "less than",
    less_than_or_equal: "at most",
    equals: "equal to",
    contains: "containing",
    string_equals: "equal to",
    begins_with: "starting with",
    ends_with: "ending with",
    exclude: "not containing"
};

// View-shaping actions are page-declared (controller.availableActions(), threaded through as
// app_context.table.availableActions — see tableCatalog's buildTableContext). An action the page never wired
// must become a plain "I can't do that here", NEVER a silent no-op that Otto reports as done (the executor
// would find no controller method on the controller and just skip it — see tableExecutor's CALLS).
const VIEW_SHAPING = new Set(["search", "clear_search", "sort_recent", "goto_column", "reset_all", "open_filters", "open_applied_filters"]);
const isActionAvailable = (action, tableCtx) => !VIEW_SHAPING.has(action) || ((tableCtx && tableCtx.availableActions) || []).includes(action);
const UNAVAILABLE_MESSAGE = {
    search: "This page doesn't support searching the table.",
    clear_search: "This page doesn't support searching the table.",
    sort_recent: "This page doesn't support sorting by recent activity.",
    goto_column: "This page doesn't support jumping to a column.",
    reset_all: "This page doesn't have a single reset-everything action.",
    open_filters: "This page doesn't have a filters panel to open.",
    open_applied_filters: "This page doesn't have an applied-filters summary to show."
};

// Which "recently touched" axis the user means. The captured value is usually already clean ("created" /
// "edited" — the enum RECENT_SORT_AXES lists), so an exact match wins outright; otherwise fall back to a
// token search across the value phrase AND the utterance for an edit-ish or creation-ish word. Both/neither
// found -> null (genuinely ambiguous or unrecognized), so the proposal CLARIFIES instead of guessing between
// two axes that read very differently on screen — the same "never a wrong action" rule column/value grounding
// follows, not the silent default a first pass considered.
const EDIT_TOKENS = ["edited", "edit", "updated", "update", "modified", "modify", "changed", "change"];
const CREATE_TOKENS = ["created", "create", "new", "added", "add"];
const hasAnyToken = (text, tokens) => {
    const norm = ` ${normalizeText(text)} `;
    return norm.trim() ? tokens.some(t => norm.includes(` ${t} `)) : false;
};
const groundRecentAxis = (value, utterance) => {
    const v = normalizeText(value);
    if (RECENT_SORT_AXES.includes(v)) return v;
    const edited = hasAnyToken(value, EDIT_TOKENS) || hasAnyToken(utterance, EDIT_TOKENS);
    const created = hasAnyToken(value, CREATE_TOKENS) || hasAnyToken(utterance, CREATE_TOKENS);
    if (edited && !created) return "edited";
    if (created && !edited) return "created";
    return null;
};

const NUMERIC_TYPES = ["number", "currency", "year"];
// Shared with the wildcard pill (core/numberFormat): separators for quantities, none for years. This list
// already named "year" as numeric and then printed it as "1,990" anyway.
const fmtWildcardValue = (value, type) =>
    NUMERIC_TYPES.includes(type) ? formatNumericValue(value, { type }) : String(value);

// Join labels conversationally: "A", "A or B", "A, B, or C".
const joinLabels = labels => {
    const l = (labels || []).filter(Boolean);
    if (l.length <= 1) return l[0] || "";
    if (l.length === 2) return `${l[0]} or ${l[1]}`;
    return `${l.slice(0, -1).join(", ")}, or ${l[l.length - 1]}`;
};

// Snap a requested row count to the nearest supported page size (the table only offers a fixed set).
const nearestPageSize = n =>
    PAGE_SIZE_OPTIONS.reduce((best, s) => (Math.abs(s - n) < Math.abs(best - n) ? s : best), PAGE_SIZE_OPTIONS[0]);

// Which columns an action can sensibly target: show → hidden columns; unpin → pinned columns; everything
// else → visible columns. Falls back to ALL columns when the preferred subset is empty, so grounding always
// has something to match (the executor no-ops if the column is already in the requested state).
const candidatesForAction = (action, columns) => {
    let subset;
    if (action === "show_column") subset = columns.filter(c => !c.isVisible);
    else if (action === "unpin_column") subset = columns.filter(c => c.pinned);
    // Row filtering targets a FILTERABLE column (has options or a wildcard), regardless of visibility —
    // you can filter by a column you've hidden. Falls back to all columns if the flags aren't populated.
    else if (action === "column_filter" || action === "clear_column_filter") subset = columns.filter(c => c.filterKind && c.filterKind !== "none");
    else subset = columns.filter(c => c.isVisible);
    return subset.length ? subset : columns;
};

// Human phrasing for one grounded change (used to build the confirm/summary line).
export const describeTableChange = change => {
    const col = change.column ? change.column.label : "";
    switch (change.action) {
        case "sort":
            return `Sort by ${col}${change.value ? ` (${change.value === "desc" ? "descending" : "ascending"})` : ""}`;
        case "hide_column":
            return `Hide the ${col} column`;
        case "show_column":
            return `Show the ${col} column`;
        case "pin_column":
            return `Pin the ${col} column`;
        case "unpin_column":
            return `Unpin the ${col} column`;
        case "unpin_all":
            return "Unpin all columns";
        case "set_page_size":
            return `Show ${change.value} rows per page`;
        case "reset_sort":
            return "Reset the sorting";
        case "reset_filters":
            return "Clear the table's local filters";
        case "reset_table":
            return "Reset the columns to default";
        case "export": {
            if (change.mode === "advanced") return "Open the export settings";
            const soft = change.includeSoftCost ? ", incl. Project Cost Total" : "";
            if (change.mode === "variant") {
                const bits = [FORMAT_LABEL[change.value] || change.value, change.breakdown ? `by ${change.breakdown}` : null]
                    .filter(Boolean)
                    .join(", ");
                return `Export using ${change.variantLabel || change.variant} (${bits}${soft})`;
            }
            return `Export the table as ${FORMAT_LABEL[change.value] || change.value}${soft}`;
        }
        case "column_filter":
            if (change.mode === "wildcard") {
                const opLabel = WILDCARD_OP_LABEL[change.operator] || change.operator || "";
                return `Filter ${col} ${opLabel} ${fmtWildcardValue(change.value, change.type)}`.replace(/\s+/g, " ").trim();
            }
            return `Filter ${col} to ${(change.values || []).join(", ")}`;
        case "clear_column_filter":
            return `Clear the ${col} filter`;
        case "search":
            return `Search the table for "${change.value}"`;
        case "clear_search":
            return "Clear the search";
        case "sort_recent":
            return `Sort by most recently ${change.value === "edited" ? "edited" : "created"}`;
        case "goto_column":
            return `Jump to the ${col} column`;
        case "reset_all":
            return "Reset all filters and sorting";
        case "open_filters":
            return "Open the filters panel";
        case "open_applied_filters":
            return "Show the applied filters";
        default:
            return "Update the table";
    }
};

// `pendingQuestion` (optional) makes the clarification ANSWERABLE instead of a dead sentence: it declares
// the answer space ({ kind, multi, options, payload }) that the pending-question protocol stores, so the
// user's next message — "all of them", "the first one", "CFD" — resolves against THIS question (typed or
// via the card's chips) instead of re-entering the pipeline cold. Kinds the resolver understands:
//   table_values  — multi-pick option values for one column (payload.column); merged into one column_filter
//   table_column  — single-pick column; the ORIGINAL raw changes re-ground with the picked column
//   table_changes — single-pick where each option carries the exact `changes` it runs (formats, page sizes,
//                   the sort_recent axis)
// (dashboardFilters' browse card adds "field_options" — declared in questionFromProposal, not here.)
const clarify = (base, message, options = [], pendingQuestion = null) => ({
    ...base,
    table: true,
    needsClarification: true,
    changes: [],
    autoApply: false,
    options,
    message,
    ...(pendingQuestion && (pendingQuestion.options || []).length ? { pendingQuestion } : {})
});

const optName = o => (o && o.name != null ? o.name : o && o.id != null ? o.id : "");

// An EXPORT-MODE choice: the page offers both a simple one-click export and its own export chooser, and the
// user didn't say which. Otto asks; the pick IS the confirmation — each button carries the `changes` it runs
// (same "the pick is the confirmation" rule as pageChoice), so there's no second confirm and no round-trip
// through the interpreter.
const exportChoice = (base, choice) => ({
    ...base,
    table: true,
    needsClarification: false,
    changes: [],
    autoApply: false,
    exportChoices: choice.choices,
    message: choice.message
});

// Is this proposal an OUTSTANDING export choice — buttons offered, nothing resolved to run yet? Note it is
// deliberately NOT `needsClarification` (that flag means "the request couldn't be grounded"; here everything
// is grounded, the user just has a decision to make), so the two callers that must treat it as a decision
// check this shape instead: the card (which renders the buttons rather than an Apply) and the PLAN LOOP,
// which pauses on it. The plan loop used to infer "nothing to apply → take the simple one", which silently
// exported a file the user never picked; one exported predicate keeps that reading from coming back.
export const isExportChoice = proposal =>
    !!(proposal && proposal.table && (proposal.exportChoices || []).length && !(proposal.changes || []).length);

// Resolve an `export` change against what the PAGE can really do ({ formats, advanced, variants }) plus the
// user's own words. Decision ladder — everything the user already SAID wins before anything is offered:
//   1. a NAMED variant ("normal grouping", "criticality based", "by building") -> that variant, its axes
//      (format / breakdown / Project Cost Total) resolved from the same words, chooser defaults otherwise
//   2. an explicit settings/chooser ask                 -> the advanced change (open the page's own popup)
//   3. a format only variants build ("as word")         -> the variant(s) that CAN; several -> their choice;
//      none (and no chooser) -> today's fallbacks — never a silent downgrade to Excel
//   4. nothing pinned on a page with several exports    -> { choice }: one button per REAL export (simple +
//      each variant + the settings chooser), each carrying the changes it runs (pick = confirmation)
// A variant-less page keeps the original behavior exactly (simple export / two-way choice / chooser).
const resolveExport = (change, exportOptions, utterance) => {
    const opts = exportOptionsOf(exportOptions);
    // The page offers this user no export (permission off, or nothing wired) — say so rather than proposing a
    // button that would silently do nothing.
    if (!opts.enabled) return { clarify: { message: "There's no export available on this page.", options: [] } };
    // The format from the interpreter's capture, else from the user's own words (deterministic, like
    // detectExportMode — the capture can miss a format the variant path still needs to honor).
    const captured = String(change.value || "").toLowerCase() || detectExportFormat(utterance) || "";
    const asked = detectExportMode(utterance);
    const variants = opts.variants || [];
    const includeSoftCost = detectIncludeSoftCost(utterance); // popup default OFF; opt-in by phrase
    const simpleLabel = `Simple ${FORMAT_LABEL[opts.formats[0]] || "Excel"}`;
    const advancedChange = { action: "export", column: null, value: null, mode: "advanced" };
    const simpleChange = format => ({
        action: "export",
        column: null,
        value: format,
        mode: "simple",
        ...(includeSoftCost ? { includeSoftCost: true } : {})
    });
    // One variant run, fully resolved: the requested format when the variant builds it (else its first —
    // the chooser's default radio), the named breakdown (else the variant's default), the soft-cost toggle.
    const variantChange = (v, format, breakdown) => ({
        action: "export",
        column: null,
        value: v.formats.includes(format) ? format : v.formats[0],
        mode: "variant",
        variant: v.key,
        variantLabel: v.label,
        ...(v.breakdowns.length ? { breakdown: breakdown || groundExportBreakdown(utterance, v) || v.breakdowns[0] } : {}),
        ...(includeSoftCost ? { includeSoftCost: true } : {})
    });

    // 1) The user NAMED a variant — that's the whole decision; resolve its axes and run it (confirm-first).
    if (variants.length) {
        const named = groundExportVariants(utterance, variants);
        if (named.length === 1) {
            const v = named[0];
            // A format the variant genuinely can't build ("crosswalk as word") — say so, never swap silently.
            // Answerable: "yes"/"excel" runs the variant with a format it CAN build.
            if (captured && EXPORT_FORMATS.includes(captured) && !v.formats.includes(captured)) {
                return {
                    clarify: {
                        message: `${v.label} exports as ${joinLabels(v.formats.map(f => FORMAT_LABEL[f]))}, not ${FORMAT_LABEL[captured]}. Want it as ${joinLabels(v.formats.map(f => FORMAT_LABEL[f]))}?`,
                        options: v.formats.map(f => FORMAT_LABEL[f]),
                        pendingQuestion: {
                            kind: "table_changes",
                            multi: false,
                            options: v.formats.map(f => ({ id: f, label: FORMAT_LABEL[f], changes: [variantChange(v, f)] }))
                        }
                    }
                };
            }
            return { change: variantChange(v, captured) };
        }
        if (named.length > 1) {
            return {
                choice: {
                    message: `Which export — ${joinLabels(named.map(v => v.label))}?`,
                    choices: named.map(v => ({ kind: v.key, label: v.label, changes: [variantChange(v, captured)] }))
                }
            };
        }
        // No variant named, but a breakdown was ("export by building") — exactly one variant owns that axis.
        const byBreakdown = variants.filter(v => groundExportBreakdown(utterance, v));
        if (byBreakdown.length === 1) {
            return { change: variantChange(byBreakdown[0], captured) };
        }
    }

    // 2) The user explicitly wants the settings/chooser (and the page has one).
    if (asked === "advanced" && opts.advanced) {
        return { change: advancedChange };
    }

    const g = groundExportFormat(captured, opts);
    // 3) A real format the SIMPLE export can't build (Recommendations builds Word only inside its chooser).
    // A variant that CAN build it runs directly; several such variants -> their choice; none -> the chooser
    // (today's behavior) — never a silent downgrade to Excel.
    if (g.decision === "advanced") {
        const able = variants.filter(v => v.formats.includes(captured));
        if (able.length === 1) return { change: variantChange(able[0], captured) };
        if (able.length > 1) {
            return {
                choice: {
                    message: `I can build a ${FORMAT_LABEL[captured]} file with ${joinLabels(able.map(v => v.label))}. Which would you like?`,
                    choices: able.map(v => ({ kind: v.key, label: v.label, changes: [variantChange(v, captured)] }))
                }
            };
        }
        return { change: advancedChange };
    }
    if (g.decision === "clarify") {
        return {
            clarify: {
                message: `Which format — ${joinLabels(g.formats.map(f => FORMAT_LABEL[f]))}?`,
                options: g.formats.map(f => FORMAT_LABEL[f]),
                pendingQuestion: {
                    kind: "table_changes",
                    multi: false,
                    options: g.formats.map(f => ({ id: f, label: FORMAT_LABEL[f], changes: [simpleChange(f)] }))
                }
            }
        };
    }

    // 4) Format settled, mode unsaid. Offer what the page REALLY has: its named variants when declared
    // (one button per export + the settings chooser), else the original simple-vs-chooser two-way.
    if (asked !== "simple" && variants.length) {
        return {
            choice: {
                message: `This page has several exports — ${joinLabels([simpleLabel, ...variants.map(v => v.label)])} — or I can open the export settings so you can configure one yourself. Which would you like?`,
                choices: [
                    { kind: "simple", label: simpleLabel, changes: [simpleChange(g.format)] },
                    ...variants.map(v => ({ kind: v.key, label: v.label, changes: [variantChange(v, g.format)] })),
                    ...(opts.advanced ? [{ kind: "advanced", label: "Export settings", changes: [advancedChange] }] : [])
                ]
            }
        };
    }
    if (opts.advanced && asked !== "simple") {
        return {
            choice: {
                message: `I can export this as a ${simpleLabel} file, or open the export settings so you can choose the export type and its options. Which would you like?`,
                choices: [
                    { kind: "simple", label: simpleLabel, changes: [simpleChange(g.format)] },
                    { kind: "advanced", label: "Advanced Export", changes: [advancedChange] }
                ]
            }
        };
    }
    return { change: simpleChange(g.format) };
};

// Resolve a column_filter change against a GROUNDED column: a numeric/text THRESHOLD (wildcard) or a set of
// discrete OPTION values grounded against the column's REAL options (fetched via the page's tableController —
// same "ground the value against real data" contract the filter path uses). Returns { change } to apply, or
// { clarify:{message,options} } when the value is ambiguous / unknown (never a wrong filter).
const resolveColumnFilter = async (change, col, ctx) => {
    const label = col.label;
    const values = Array.isArray(change.values) ? change.values.map(v => (v == null ? "" : String(v).trim())).filter(Boolean) : [];
    const single = change.value != null && String(change.value).trim() ? String(change.value).trim() : null;
    const numericCol = NUMERIC_TYPES.includes(col.type);

    // WILDCARD (threshold) path: a numeric column, a single phrase carrying an operator ("over 100000"), or a
    // wildcard-only column. Discrete `values` always take the OPTIONS path.
    if (!values.length && single != null && (numericCol || hasThresholdOperator(single) || col.filterKind === "wildcard")) {
        const parsed = parseWildcardValue(single, col.type);
        if (!parsed) return { clarify: { message: `What value should I filter ${label} by?`, options: [] } };
        return { change: { action: "column_filter", column: { key: col.key, label }, mode: "wildcard", operator: parsed.operator, value: parsed.value, type: col.type } };
    }

    const phrases = values.length ? values : single != null ? [single] : [];
    if (!phrases.length) return { clarify: { message: `What value should I filter ${label} by?`, options: [] } };

    const fetched = ctx.tableController ? await ctx.tableController.getColumnOptions(col.key) : [];
    const optList = Array.isArray(fetched) ? fetched : [];
    if (!optList.length) {
        // Options couldn't be loaded (endpoint hiccup / a column with none). column_filter is confirm-first,
        // so surface the user's literal value(s) on the card — nothing auto-applies without their click.
        return { change: { action: "column_filter", column: { key: col.key, label }, mode: "options", values: phrases } };
    }

    // A value question is MULTI-pick and answerable: "all of them" (every candidate is wanted — the
    // "florida" → three Florida divisions case), "CFD and WFD", "the first one" all resolve against these
    // options via the pending-question protocol instead of looping the same question.
    const valuesQuestion = names => ({
        kind: "table_values",
        multi: true,
        options: names.map(n => ({ id: n, label: n })),
        payload: { column: { key: col.key, label } }
    });
    const names = [];
    for (const phrase of phrases) {
        const gv = groundValue(phrase, optList);
        if (gv.decision === "auto" && gv.value) {
            names.push(optName(gv.value));
        } else if (gv.decision === "clarify" && (gv.candidates || []).length) {
            const cands = gv.candidates.map(optName).filter(Boolean);
            return { clarify: { message: `Which ${label} did you mean — ${joinLabels(cands)}?`, options: cands, pendingQuestion: valuesQuestion(cands) } };
        } else {
            const sample = optList.slice(0, 8).map(optName).filter(Boolean);
            return { clarify: { message: `I couldn't find “${phrase}” in ${label}. Try: ${joinLabels(sample)}.`, options: sample, pendingQuestion: valuesQuestion(sample) } };
        }
    }
    return { change: { action: "column_filter", column: { key: col.key, label }, mode: "options", values: names } };
};

// interpretResponse = { request_id, session_id, intent_kind: "table_command", table: TableCommand };
// ctx = { utterance, appContext, tableController }. The model only CAPTURES the operation + a column PHRASE
// (+ value phrase(s) for column_filter); WHICH real column/value those are is grounded HERE against
// app_context.table.columns and, for row filters, the column's real options (via the tableController). Async
// because option grounding fetches the column's real values.
export const buildTableProposal = async (interpretResponse, ctx = {}) => {
    const base = {
        requestId: interpretResponse && interpretResponse.request_id,
        sessionId: interpretResponse && interpretResponse.session_id,
        utterance: ctx.utterance
    };
    const command = (interpretResponse && interpretResponse.table) || {};
    const rawChanges = command.changes || [];
    const tableCtx = (ctx.appContext && ctx.appContext.table) || {};
    const columns = buildTableColumns(tableCtx);
    // What this page's export can really produce — declared by the page via its tableController, not assumed.
    const exportOptions = exportOptionsOf(tableCtx.exportOptions);

    // Model asked to clarify (no concrete operation, e.g. "change the table").
    if (command.needs_clarification || rawChanges.length === 0) {
        const c = command.clarification;
        const q = (c && c.question) || command.assistant_message || "What would you like to do with the table?";
        const opts = (c && c.options) || [];
        return clarify(base, opts.length ? `${q} (${opts.join(" / ")})` : q, opts);
    }

    const resolved = [];
    // What we had to SKIP because the page never wired it. Kept as notes rather than turned into a
    // clarification: unlike every other clarify in this file, no answer could ever wire a missing controller
    // method, so asking would be terminal, not paused — and it would throw away the sibling changes this page
    // CAN do ("search for boilers and sort by cost" losing the sort forever). Dropping is not guessing, so the
    // ground-or-ask rule still holds: we propose only what we grounded, and say plainly what we couldn't.
    const skipped = [];
    for (const change of rawChanges) {
        const spec = TABLE_ACTIONS[change.action];
        if (!spec) continue; // unknown action — drop defensively

        // A view-shaping action the page never wired (no controller dep -> absent from availableActions):
        // drop just THAT change and report it, rather than proposing something the executor would silently
        // no-op on. If nothing survives, the notes become the clarification (below).
        if (!isActionAvailable(change.action, tableCtx)) {
            const note = UNAVAILABLE_MESSAGE[change.action] || "This page doesn't support that.";
            if (!skipped.includes(note)) skipped.push(note); // one sentence per reason, not per change
            continue;
        }

        if (spec.needsColumn) {
            // A column question re-grounds the ORIGINAL raw changes with the picked column substituted for
            // the phrase that failed — so answering "Construction Cost" re-runs the whole "sort by cost"
            // request, not just names a column into the void.
            const columnQuestion = cands => ({
                kind: "table_column",
                multi: false,
                options: cands.map(c => ({ id: c.key, label: c.label })),
                payload: { changes: rawChanges, failedColumn: change.column || "" }
            });
            const g = groundColumn(change.column || "", candidatesForAction(change.action, columns));
            if (!(g.decision === "auto" && g.column)) {
                if (g.decision === "clarify" && (g.candidates || []).length) {
                    return clarify(
                        base,
                        `Which column do you mean — ${joinLabels(g.candidates.map(c => c.label))}?`,
                        g.candidates.map(c => c.label),
                        columnQuestion(g.candidates)
                    );
                }
                const phrase = (change.column || "").trim();
                // A row filter where the model named a VALUE but no column ("only critical") — ask which column.
                if (change.action === "column_filter" && !phrase) {
                    const filterable = candidatesForAction("column_filter", columns);
                    const v = (change.values && change.values.join(", ")) || change.value || "that";
                    return clarify(base, `Which column should I filter to “${v}”?`, filterable.map(c => c.label), columnQuestion(filterable));
                }
                return clarify(
                    base,
                    phrase ? `I couldn't find a “${phrase}” column in the table.` : "Which column?",
                    columns.slice(0, 8).map(c => c.label),
                    columnQuestion(columns.slice(0, 8))
                );
            }
            // Grounded column in hand — row-filter actions resolve their value(s); the rest carry the column.
            if (change.action === "clear_column_filter") {
                resolved.push({ action: "clear_column_filter", column: { key: g.column.key, label: g.column.label } });
                continue;
            }
            if (change.action === "column_filter") {
                const r = await resolveColumnFilter(change, g.column, ctx);
                if (r.clarify) {
                    // Enrich the answer space with the ORIGINAL raw changes + which one failed: answering
                    // re-runs the WHOLE request with the picked values substituted, so sibling changes
                    // ("…and export it") survive the question instead of being dropped with it.
                    const pq = r.clarify.pendingQuestion
                        ? {
                              ...r.clarify.pendingQuestion,
                              payload: {
                                  ...r.clarify.pendingQuestion.payload,
                                  changes: rawChanges,
                                  failedIndex: rawChanges.indexOf(change)
                              }
                          }
                        : null;
                    return clarify(base, r.clarify.message, r.clarify.options, pq);
                }
                resolved.push(r.change);
                continue;
            }
            resolved.push({ action: change.action, column: { key: g.column.key, label: g.column.label }, value: change.value || null });
            continue;
        }

        if (change.action === "set_page_size") {
            const n = parseInt(change.value, 10);
            if (!Number.isFinite(n) || n <= 0) {
                return clarify(base, `How many rows per page? (${PAGE_SIZE_OPTIONS.join(", ")})`, PAGE_SIZE_OPTIONS.map(String), {
                    kind: "table_changes",
                    multi: false,
                    options: PAGE_SIZE_OPTIONS.map(s => ({
                        id: String(s),
                        label: String(s),
                        changes: [{ action: "set_page_size", column: null, value: String(s) }]
                    }))
                });
            }
            resolved.push({ action: change.action, column: null, value: String(nearestPageSize(n)) });
            continue;
        }

        if (change.action === "export") {
            const r = resolveExport(change, exportOptions, ctx.utterance);
            if (r.clarify) return clarify(base, r.clarify.message, r.clarify.options, r.clarify.pendingQuestion);
            if (r.choice) return exportChoice(base, r.choice);
            resolved.push(r.change);
            continue;
        }

        // Free-text search has no column and no catalog to ground against — carry the phrase as-is, unless
        // it's empty (nothing to search for).
        if (change.action === "search") {
            const text = change.value != null ? String(change.value).trim() : "";
            if (!text) return clarify(base, "What would you like to search for?", []);
            resolved.push({ action: "search", column: null, value: text });
            continue;
        }

        // sort_recent's value must ground to one of RECENT_SORT_AXES; an unrecognized phrase asks (answerably)
        // rather than guessing which of the two very-different sorts was meant.
        if (change.action === "sort_recent") {
            const axis = groundRecentAxis(change.value, ctx.utterance);
            if (!axis) {
                return clarify(base, "Sort by most recently edited, or most recently created?", ["Most recently edited", "Most recently created"], {
                    kind: "table_changes",
                    multi: false,
                    options: [
                        { id: "edited", label: "Most recently edited", changes: [{ action: "sort_recent", column: null, value: "edited" }] },
                        { id: "created", label: "Most recently created", changes: [{ action: "sort_recent", column: null, value: "created" }] }
                    ]
                });
            }
            resolved.push({ action: "sort_recent", column: null, value: axis });
            continue;
        }

        // Value-less global actions: unpin_all, reset_sort, reset_filters, reset_table, clear_search, reset_all,
        // open_filters, open_applied_filters.
        resolved.push({ action: change.action, column: null, value: null });
    }

    // Nothing survived: the skip notes ARE the answer ("This page doesn't support searching the table."),
    // falling back to the generic ask only when there was nothing to skip either.
    if (!resolved.length) {
        return clarify(base, skipped.length ? skipped.join(" ") : "What would you like to do with the table?");
    }

    // Auto-apply only when EVERY resolved op is a cheap/reversible one; a reset or export in the mix makes
    // the whole proposal confirm-first (safer than partially auto-applying).
    const autoApply = resolved.every(c => TABLE_ACTIONS[c.action].autoApply);
    // Anything skipped is said FIRST, so the user is never left thinking the whole request went through.
    const summary = `${resolved.map(describeTableChange).join(", and ")}?`;
    const message = skipped.length ? `${skipped.join(" ")} ${summary}` : summary;
    return { ...base, table: true, needsClarification: false, changes: resolved, autoApply, message };
};
